import {
  Box,
  Button,
  Link,
  Skeleton,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import {AiOutlineInfoCircle} from 'react-icons/ai';
import {Tooltip} from '@jobseeker/lotus/elements';
import Avatar from '@jobseeker/lotus/components/Avatar';
import ProChip from '@jobseeker/lotus/elements/Pro';
import {isCSR} from '@jobseeker/lotus/utils';

import type {JobOverviewCardProps} from '../type';
import {useIsCustomWidth, useIsLargeDevice} from '@jobseeker/lotus/hooks';
import moment from 'moment';
import { ChevronDownDark, ConfiTag, VerifiedRecruiter, ChevronUpDark} from '@jobseeker/lotus/assets/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {useState} from 'react';


const isAppHirist = process.env.NEXT_PUBLIC_APP_NAME === 'hirist';

const CounterBox = styled(Box)(({theme, ...props}: any) => ({
  textAlign: 'center',
  maxWidth: 85,
  wordBreak: 'break-word',
  '.CounterDigits': {},
  [theme.breakpoints.down('xxs')]: {
    width: 71,
  },
  [theme.breakpoints.down('xs')]: {
    width: 75,
  },
  [theme.breakpoints.down('ld')]: {
    lg: 'fit-content',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: 'unset',
    textAlign: 'left',
    lineHeight: '1rem',
  },
}));
export default ({
  showDetails = true,
  showRecommended = false,
  showReadMore = false,
  isExpanded,
  onToggle,
  ...props
}: JobOverviewCardProps) => {
  const theme = useTheme();
  const isLarge = useIsLargeDevice();
  const isSmall = useIsCustomWidth('sm');

  // Use props for expansion state if provided, otherwise fallback to local state
  const [localExpanded, setLocalExpanded] = useState(
    showReadMore === true ? false : true
  );

  const expanded =
    showReadMore && isExpanded !== undefined ? isExpanded : localExpanded;
  const handleReadMore = () => {
    if (showReadMore && onToggle) {
      onToggle();
    } else {
      setLocalExpanded(prev => !prev);
    }
  };
  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      rowGap={2.2}
      width={'100%'}
      sx={{
        '@media (width:480px)': {
          margin: 'auto',
        },
      }}
    >
      <Box data-testid="job-overview-card">
        <Typography
          color="text.secondary"
          variant="body2"
          fontSize={showReadMore ? 12 : 14}
        >
          {props.cardTitle ?? 'Posted By'}
        </Typography>
        <Box display={'flex'} columnGap={1} mt={1.3} alignItems={'center'}>
          <Box
            sx={{
              // borderRadius: '100%',
              minWidth: 50,
              width: 50,
              height: 50,
              // overflow: 'hidden',
            }}
          >
            <Avatar
              imgUrl={props.recruiterImgUrl}
              sx={{
                width: 50,
                height: 50,
                ...(props?.confidential
                  ? {border: '1px solid #7E7E7E33', color: '#7E7E7E33'}
                  : {}),
              }}
            />
          </Box>

          <Box>
            {props.confidential ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: 0.5,
                }}
              >
                <Typography
                  component={'p'}
                  lineHeight={{
                    xxs: isAppHirist ? '24px' : '16px',
                    md: isAppHirist ? '24px' : '20px',
                  }}
                  color="text.primary"
                  variant="subtitle2"
                  fontSize={{
                    xxs: isAppHirist ? '18px' : '10px',
                    md: isAppHirist ? '18px' : '14px',
                  }}
                  fontWeight={isAppHirist ? 600 : 500}
                  sx={{
                    background: isAppHirist
                      ? 'linear-gradient(90deg, #E9630C 0%, #FFAD02 98.08%)'
                      : 'linear-gradient(90deg, #225448 0%, #7E9F67 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Verified Recruiter
                </Typography>
                <Tooltip
                  id="Verified_Recruiter_tag"
                  data-testid="Verified_Recruiter_tag"
                  title={
                    <Typography variant="body2">
                      This is a confidential job posted by a Verified Recruiter.
                      While the company name is hidden, the role has been
                      verified by {!isAppHirist ? 'iimjobs.com' : 'hirist.tech'}
                      .{' '}
                      <span
                        style={{
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          props.setOpenConfJobDialog(true);
                        }}
                      >
                        Learn More
                      </span>
                    </Typography>
                  }
                  arrow
                >
                  <Image
                    className="verified_recruiter_tag"
                    src={isAppHirist ? VerifiedRecruiter : ConfiTag}
                    alt={'premium_icon'}
                    width={isSmall ? 12 : 17}
                    height={isSmall ? 12 : 17}
                    style={{
                      objectFit: 'cover',
                    }}
                  />
                </Tooltip>
              </Box>
            ) : (
              <Link href={props.actionUrl ?? '#'} sx={{textDecoration: 'none'}}>
                <Typography
                  component={'p'}
                  lineHeight={{xxs: '16px', md: '20px'}}
                  color="text.primary"
                  variant="subtitle2"
                  fontSize={showReadMore ? 12 : 14}
                >
                  {props.recruiterName}
                </Typography>
              </Link>
            )}

            <Typography
              component={'p'}
              lineHeight={'16px'}
              color="text.primary"
              fontSize={{xxs: 12}}
              fontWeight={500}
              variant="subtitle3"
            >
              <Link
                href={props.actionUrl ?? '#'}
                sx={{textDecoration: 'none', color: 'text.primary'}}
              >
                {props.position}
              </Link>
            </Typography>
            {props.lastActive && (
              <Typography
                component={'p'}
                lineHeight={'16px'}
                fontSize={{xxs: 12}}
                variant="subtitle3"
                fontWeight={400}
                color="text.secondary"
              >
                Last Active: {props.lastActive}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        display={'block'}
        borderRadius={4}
        sx={{
          background: '#F6F7F8',
          '@media (width:480px)': {},
        }}
      >
        {isLarge ? (
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            px={{xxs: 1.25, lg: 2}}
            columnGap={{xxs: 3}}
            py={2}
            pb={props?.disableInsights ? 2 : {xxs: 2, md: 0}}
            data-testid="job-overview-card"
          >
            <CounterBox>
              <Typography
                component={'p'}
                fontFamily={'Abhaya Libre'}
                className="CounterDigits"
                fontSize={
                  showReadMore
                    ? {xxs: 10, md: 18}
                    : {xxs: 10, md: 20, lg: 24, xl: isAppHirist ? 22 : 32}
                }
                fontWeight={700}
                lineHeight={{xxs: 'unset', md: showReadMore ? '22px' : '37px'}}
              >
                {props.jobViews}
              </Typography>
              <Typography
                color="text.secondary"
                component={'p'}
                fontSize={{xxs: 8, xl: isAppHirist ? 11 : 12}}
                textAlign={'center'}
                fontWeight={500}
              >
                JOB VIEWS
              </Typography>
            </CounterBox>
            <CounterBox>
              <Typography
                component={'p'}
                fontFamily={'Abhaya Libre'}
                className="CounterDigits"
                fontSize={
                  showReadMore
                    ? {xxs: 10, md: 18}
                    : {xxs: 10, md: 20, lg: 24, xl: isAppHirist ? 22 : 32}
                }
                fontWeight={700}
                lineHeight={{xxs: 'unset', md: showReadMore ? '22px' : '37px'}}
              >
                {props.applications}
              </Typography>
              <Typography
                color="text.secondary"
                component={'p'}
                fontSize={{xxs: 8, xl: isAppHirist ? 11 : 12}}
                textAlign="center"
                fontWeight={500}
              >
                APPLICATIONS
              </Typography>
            </CounterBox>
            <CounterBox>
              <Typography
                component={'p'}
                fontFamily={'Abhaya Libre'}
                className="CounterDigits"
                fontSize={
                  showReadMore
                    ? {xxs: 10, md: 18}
                    : {xxs: 10, md: 20, lg: 24, xl: isAppHirist ? 22 : 32}
                }
                fontWeight={700}
                lineHeight={{xxs: 'unset', md: showReadMore ? '22px' : '37px'}}
              >
                {props.recruiterActions}
              </Typography>
              <Typography
                color="text.secondary"
                component={'p'}
                fontSize={{xxs: 8, xl: isAppHirist ? 11 : 12}}
                textAlign="center"
                fontWeight={500}
              >
                RECRUITER ACTIONS
              </Typography>
            </CounterBox>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              columnGap: 3,
              py: {xxs: 1, xs: 1.25},
              px: {xxs: 1, xs: 1.5},
            }}
            data-testid="job-overview-card"
          >
            <CounterBox>
              <Typography
                color="text.secondary"
                component={'span'}
                fontSize={{xxs: 8, xs: 12, xl: 12}}
                // textAlign={'center'}
                fontWeight={500}
                sx={{wordBreak: 'keep-all'}}
              >
                Job Views: &nbsp;
                <Box
                  component={'br'}
                  sx={{display: {xxs: 'block', xs: 'none'}}}
                />
              </Typography>
              <Typography
                component={'span'}
                fontFamily={'Abhaya Libre'}
                className="CounterDigits"
                fontSize={{
                  xxs: 10,
                  xs: 16,
                  md: 20,
                  lg: 24,
                  xl: isAppHirist ? 22 : 32,
                }}
                fontWeight={700}
                lineHeight={{xxs: 'unset', md: '37px'}}
              >
                {props.jobViews}
              </Typography>
            </CounterBox>
            <CounterBox>
              <Typography
                color="text.secondary"
                component={'span'}
                fontSize={{xxs: 8, xs: 12, xl: 12}}
                textAlign={'left'}
                fontWeight={500}
                sx={{wordBreak: 'keep-all'}}
              >
                Applications: &nbsp;
              </Typography>
              <Typography
                component={'span'}
                fontFamily={'Abhaya Libre'}
                className="CounterDigits"
                fontSize={{
                  xxs: 10,
                  xs: 16,
                  md: 20,
                  lg: 24,
                  xl: isAppHirist ? 22 : 32,
                }}
                fontWeight={700}
                lineHeight={{xxs: 'unset', md: '37px'}}
              >
                {props.applications}
              </Typography>
            </CounterBox>
            <CounterBox>
              <Typography
                color="text.secondary"
                component={'span'}
                fontSize={{xxs: 8, xs: 12, xl: 12}}
                textAlign={'left'}
                fontWeight={500}
                sx={{wordBreak: 'keep-all'}}
              >
                Recruiter Actions: &nbsp;
              </Typography>
              <Typography
                component={'span'}
                fontFamily={'Abhaya Libre'}
                className="CounterDigits"
                fontSize={{
                  xxs: 10,
                  xs: 16,
                  md: 20,
                  lg: 24,
                  xl: isAppHirist ? 22 : 32,
                }}
                fontWeight={700}
                lineHeight={{xxs: 'unset', md: '37px'}}
              >
                {props.recruiterActions}
              </Typography>
            </CounterBox>
          </Box>
        )}
        {!props?.disableInsights && isCSR() ? (
          <Box
            display={{xxs: 'none', md: 'block'}}
            borderRadius={2}
            mx={1}
            my={2}
            py={1}
            px={1.5}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.80)',
            }}
          >
            <Typography
              fontWeight={theme?.typography.fontWeightMedium}
              fontSize={showReadMore ? 12 : 14}
              lineHeight={1.2}
            >
              See how you stand against competition
            </Typography>
            <Box display={'flex'} alignItems={'center'}>
              <Box mt={0.25}>
                {!props?.proValid && (
                  <ProChip
                    key={'Pro0'}
                    bgColor="linear-gradient(180deg, #FFB743 0%, #F99914 100%)"
                  />
                )}
              </Box>
              <Typography
                fontWeight={theme?.typography.fontWeightMedium}
                color={'primary'}
                lineHeight={1.8}
                marginLeft={!props?.proValid ? 0.5 : 0}
                onClick={props?.openInsightsDialog}
                sx={{
                  cursor: 'pointer',
                  fontSize: showReadMore ? 12 : 16,
                }}
              >
                View Insights
              </Typography>
            </Box>
          </Box>
        ) : (
          !props?.disableInsights && (
            <Skeleton
              variant="rounded"
              width={'90%'}
              height={48}
              sx={{
                mt: {xxs: 0, md: 1},
                ml: {xxs: 2, md: 'none'},
                mb: 1,
                display: {md: 'block'},
              }}
            />
          )
        )}
      </Box>

      {showDetails && (
        <>
          {expanded && !!props?.enableMagicRank && props?.magicSortRank && (
            <Box display={{xxs: 'none', md: 'block'}}>
              <Typography
                color="text.secondary"
                variant="body2"
                display={'flex'}
                alignItems={'center'}
                gap={0.5}
              >
                Magic Sort Rank{' '}
                <Tooltip
                  arrow
                  title={
                    'We use Data Science and proprietary Machine Learning algorithms to better rank candidates who apply to a position. This will be your rank if you apply to this position. The recruiter may or may not agree with our assessment.'
                  }
                >
                  <Typography
                    component={'span'}
                    lineHeight={0}
                    color={'secondary.dark'}
                    fontSize={showReadMore ? 12 : 14}
                  >
                    <AiOutlineInfoCircle />
                  </Typography>
                </Tooltip>
              </Typography>
              <Typography
                color="text.primary"
                variant="body2"
                fontSize={showReadMore ? 12 : 14}
              >
                {props.magicSortRank}
              </Typography>
              {showRecommended && (
                <Link
                  color="primary"
                  variant="subtitle1"
                  href={isAppHirist ? '/jobfeed' : '/recommended-jobs'}
                  target="_blank"
                  sx={{
                    textDecoration: 'none',
                    fontSize: showReadMore ? 12 : 14,
                  }}
                >
                  View Recommended Jobs
                </Link>
              )}
            </Box>
          )}

          {expanded && (
            <Box display={{xxs: 'none', md: 'block'}}>
              <Typography
                color="text.secondary"
                variant="body2"
                fontSize={showReadMore ? 12 : 14}
              >
                Posted in
              </Typography>
              <a
                href={`/c/${props?.category.tag
                  ?.toLowerCase()
                  .replace(/[^A-Z0-9]+/gi, '-')}-jobs`}
                target="_blank"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <Typography
                  color="text.primary"
                  variant="body2"
                  fontSize={showReadMore ? 12 : 14}
                >
                  {props.category.label}
                </Typography>
              </a>
            </Box>
          )}

          {isAppHirist && expanded && (
            <Box display={{xxs: 'none', md: 'block'}}>
              <Typography
                color="text.secondary"
                variant="body2"
                fontSize={showReadMore ? 12 : 14}
              >
                Functional Area
              </Typography>
              <Typography
                color="text.primary"
                variant="body2"
                fontSize={showReadMore ? 12 : 14}
              >
                {props.functionalArea}
              </Typography>
            </Box>
          )}

          {expanded && (
            <Box display={{xxs: 'none', md: 'block'}}>
              <Typography
                color="text.secondary"
                variant="body2"
                fontSize={showReadMore ? 12 : 14}
              >
                Job Code
              </Typography>
              <Typography
                color="text.primary"
                variant="body2"
                fontSize={showReadMore ? 12 : 14}
              >
                {props.jobId}
              </Typography>
            </Box>
          )}
          {showReadMore && (
            <Box
              mt={'-12px'}
              display={{xxs: 'none', md: 'flex'}}
              justifyContent="space-between"
              alignItems="center"
              onClick={handleReadMore}
              sx={{cursor: 'pointer'}}
            >
              <Typography color="primary" lineHeight={2.25} variant="subtitle3">
                {expanded ? 'Show Less' : 'Show More'}
              </Typography>
              <Image
                className="chevron"
                src={expanded ? ChevronUpDark : ChevronDownDark}
                alt="ChevronDark"
                width={18}
                height={18}
                style={{
                  objectFit: 'contain',
                  alignSelf: 'center',
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
